import React, { useState } from 'react';
import { 
  Card, 
  Row, 
  Col, 
  Typography, 
  Button, 
  Modal, 
  Form, 
  Input, 
  Select, 
  DatePicker, 
  message,
  List,
  Tag,
  Space,
  Switch,
  Popconfirm
} from 'antd';
import { 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  CalendarOutlined,
  MailOutlined,
  ClockCircleOutlined
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { eventsAPI, contactsAPI } from '../../services/api';
import moment from 'moment';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;
const { RangePicker } = DatePicker;

const Events = () => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [form] = Form.useForm();

  const queryClient = useQueryClient();

  const { data: eventsData, isLoading } = useQuery('events', () => eventsAPI.getAll());
  const { data: contactsData } = useQuery('contacts', () => contactsAPI.getAll({ limit: 1000 }));
  
  const events = eventsData?.data?.data?.events || [];
  const contacts = contactsData?.data?.data?.contacts || [];

  const createEventMutation = useMutation(eventsAPI.create, {
    onSuccess: () => {
      message.success('Événement créé avec succès');
      setIsModalVisible(false);
      setEditingEvent(null);
      form.resetFields();
      queryClient.invalidateQueries('events');
    },
    onError: (error) => {
      message.error(error.response?.data?.message || 'Erreur lors de la création');
    }
  });

  const updateEventMutation = useMutation(
    ({ id, data }) => eventsAPI.update(id, data),
    {
      onSuccess: () => {
        message.success('Événement mis à jour avec succès');
        setIsModalVisible(false);
        setEditingEvent(null);
        form.resetFields();
        queryClient.invalidateQueries('events');
      },
      onError: (error) => {
        message.error(error.response?.data?.message || 'Erreur lors de la mise à jour');
      }
    }
  );

  const deleteEventMutation = useMutation(eventsAPI.delete, {
    onSuccess: () => {
      message.success('Événement supprimé avec succès');
      queryClient.invalidateQueries('events');
    },
    onError: (error) => {
      message.error(error.response?.data?.message || 'Erreur lors de la suppression');
    }
  });

  const toggleEventMutation = useMutation(
    ({ id }) => eventsAPI.toggle(id),
    {
      onSuccess: () => {
        message.success('Statut de l\'événement mis à jour');
        queryClient.invalidateQueries('events');
      },
      onError: (error) => {
        message.error(error.response?.data?.message || 'Erreur lors de la mise à jour');
      }
    }
  );

  const handleSubmit = (values) => {
    const eventData = {
      ...values,
      trigger_date: values.trigger_date ? values.trigger_date.toISOString() : null
    };

    if (editingEvent) {
      updateEventMutation.mutate({ id: editingEvent.id, data: eventData });
    } else {
      createEventMutation.mutate(eventData);
    }
  };

  const handleEdit = (event) => {
    setEditingEvent(event);
    form.setFieldsValue({
      ...event,
      trigger_date: event.trigger_date ? moment(event.trigger_date) : null,
      contact_ids: event.contacts?.map(c => c.id) || []
    });
    setIsModalVisible(true);
  };

  const handleToggle = (event) => {
    toggleEventMutation.mutate({ id: event.id });
  };

  const renderEventCard = (event) => (
    <Card
      key={event.id}
      title={
        <Space>
          <CalendarOutlined />
          {event.name}
          <Tag color={event.is_active ? 'green' : 'red'}>
            {event.is_active ? 'Actif' : 'Inactif'}
          </Tag>
        </Space>
      }
      extra={
        <Space>
          <Switch
            checked={event.is_active}
            onChange={() => handleToggle(event)}
            size="small"
          />
          <Button
            icon={<EditOutlined />}
            size="small"
            onClick={() => handleEdit(event)}
          />
          <Popconfirm
            title="Êtes-vous sûr de vouloir supprimer cet événement?"
            onConfirm={() => deleteEventMutation.mutate(event.id)}
            okText="Oui"
            cancelText="Non"
          >
            <Button
              icon={<DeleteOutlined />}
              size="small"
              danger
            />
          </Popconfirm>
        </Space>
      }
      style={{ marginBottom: 16 }}
    >
      <Text type="secondary">{event.description}</Text>
      
      <div style={{ marginTop: 12 }}>
        <Space direction="vertical" style={{ width: '100%' }}>
          <div>
            <Text strong>Type: </Text>
            <Tag color="blue">
              {event.trigger_type === 'date' ? 'Date spécifique' : 
               event.trigger_type === 'recurring' ? 'Récurrent' : 'Manuel'}
            </Tag>
          </div>
          
          {event.trigger_date && (
            <div>
              <Text strong>Date: </Text>
              <Text>{moment(event.trigger_date).format('DD/MM/YYYY HH:mm')}</Text>
            </div>
          )}
          
          {event.last_sent && (
            <div>
              <Text strong>Dernier envoi: </Text>
              <Text>{moment(event.last_sent).format('DD/MM/YYYY HH:mm')}</Text>
            </div>
          )}
          
          <div>
            <Text strong>Contacts: </Text>
            <div style={{ marginTop: 8 }}>
              {event.contacts?.map(contact => (
                <Tag key={contact.id} style={{ marginBottom: 4 }}>
                  {contact.first_name} {contact.last_name}
                </Tag>
              ))}
            </div>
          </div>
        </Space>
      </div>
    </Card>
  );

  return (
    <div>
      <div className="page-header">
        <Row justify="space-between" align="middle">
          <Col>
            <Title level={2}>Événements Automatisés</Title>
          </Col>
          <Col>
            <Button 
              type="primary" 
              icon={<PlusOutlined />}
              onClick={() => {
                setEditingEvent(null);
                form.resetFields();
                setIsModalVisible(true);
              }}
            >
              Créer un événement
            </Button>
          </Col>
        </Row>
      </div>

      <Row gutter={[16, 16]}>
        {events.map(renderEventCard)}
      </Row>

      <Modal
        title={editingEvent ? 'Modifier l\'événement' : 'Créer un événement'}
        open={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          setEditingEvent(null);
          form.resetFields();
        }}
        footer={null}
        width={700}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="name"
            label="Nom de l'événement"
            rules={[{ required: true, message: 'Veuillez saisir le nom' }]}
          >
            <Input placeholder="Ex: Anniversaire, Relance contrat" />
          </Form.Item>

          <Form.Item
            name="description"
            label="Description"
          >
            <TextArea placeholder="Description de l'événement" rows={3} />
          </Form.Item>

          <Form.Item
            name="trigger_type"
            label="Type de déclencheur"
            rules={[{ required: true, message: 'Veuillez sélectionner un type' }]}
          >
            <Select placeholder="Sélectionner le type">
              <Option value="date">Date spécifique</Option>
              <Option value="recurring">Récurrent</Option>
              <Option value="manual">Manuel</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="trigger_date"
            label="Date de déclenchement"
          >
            <DatePicker 
              showTime 
              style={{ width: '100%' }}
              placeholder="Sélectionner la date et l'heure"
            />
          </Form.Item>

          <Form.Item
            name="contact_ids"
            label="Contacts concernés"
            rules={[{ required: true, message: 'Veuillez sélectionner au moins un contact' }]}
          >
            <Select
              mode="multiple"
              placeholder="Sélectionner les contacts"
              optionFilterProp="children"
              filterOption={(input, option) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
            >
              {contacts.map(contact => (
                <Option key={contact.id} value={contact.id}>
                  {contact.first_name} {contact.last_name} ({contact.email})
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="email_template"
            label="Modèle d'email"
            rules={[{ required: true, message: 'Veuillez saisir le modèle d\'email' }]}
          >
            <TextArea 
              placeholder="Contenu de l'email (utilisez {{contact_name}} pour le nom du contact)"
              rows={6}
            />
          </Form.Item>

          <Form.Item>
            <Button 
              type="primary" 
              htmlType="submit" 
              loading={createEventMutation.isLoading || updateEventMutation.isLoading}
              block
            >
              {editingEvent ? 'Mettre à jour' : 'Créer'} l'événement
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Events;
